/**
 * API Service
 *
 * Configures Axios instance with JWT authentication interceptors,
 * automatic token refresh on 401 errors, schoolId header injection,
 * and global error handling with toast notifications.
 *
 * 27.10 / Path B-full:
 *   - withCredentials: true on the axios instance, so the httpOnly
 *     refresh-token cookie travels on /auth/refresh requests.
 *   - The refresh interceptor no longer reads or sends refresh tokens
 *     from JS state — the cookie does the work.
 *   - setTokens(accessToken, refreshToken) → setAccessToken(accessToken).
 *     Refresh tokens are server-managed end-to-end.
 *
 * @module services/api
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

/**
 * Base URL for API requests.
 * Uses environment variable or defaults to localhost in development.
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Token storage keys for localStorage.
 */
const TOKEN_KEY = 'schoolsaas_token';
const SCHOOL_ID_KEY = 'schoolsaas_school_id';
const SCHOOL_NAME_KEY = 'schoolsaas_school_name';
const USER_STORAGE_KEY = 'schoolsaas_user';

/**
 * Standard API response shape from backend.
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

/**
 * Error response shape from backend.
 */
export interface ApiError {
  success: false;
  error: string;
  message?: string;
  statusCode?: number;
}

/**
 * Flag to prevent multiple simultaneous refresh attempts.
 */
let isRefreshing = false;

/**
 * [REQ-AUG-30 M.6 / USAGE-1] The refusal-metric hook. The usage tracker (utils/usage.ts, which
 * imports THIS module) registers here at init — a callback slot rather than an import, so the
 * dependency stays one-directional and no api↔usage cycle exists. Deliberately fired from the
 * interceptor's two REFUSAL exits only: the 409-synthesis exit and the terminal 4xx reject.
 * 401-refresh internals and 5xx are NOT refusals (auth plumbing and outages, not feature walls).
 */
let usageRefusalListener: (() => void) | null = null;
export function onApiRefusal(fn: () => void): void {
  usageRefusalListener = fn;
}

/**
 * Queue of failed requests waiting for token refresh.
 */
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

/**
 * Process queued requests after token refresh.
 */
function processQueue(error: Error | null, token: string | null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else if (token) resolve(token);
  });
  failedQueue = [];
}

/**
 * Get stored access token.
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Get stored school ID.
 */
export function getSchoolId(): string | null {
  return localStorage.getItem(SCHOOL_ID_KEY);
}

/**
 * Get the stored school's display name, persisted from the last successful
 * `GET /schools/current` fetch. Used only as a login-page display label —
 * never as a source of truth for API calls (schoolId is).
 */
export function getSchoolName(): string | null {
  return localStorage.getItem(SCHOOL_NAME_KEY);
}

/**
 * Read the stored user's role, used to route auth failures correctly.
 */
function getStoredUserRole(): string | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw)?.role ?? null) : null;
  } catch {
    return null;
  }
}

/**
 * Store the access token in localStorage. The refresh token is set by
 * the server as an httpOnly cookie and is NOT a frontend concern.
 *
 * @param accessToken - JWT access token
 */
export function setAccessToken(accessToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
}

/**
 * Store school ID in localStorage. The request interceptor attaches it
 * as the `X-School-Id` header on every outbound request.
 */
export function setSchoolId(schoolId: string): void {
  localStorage.setItem(SCHOOL_ID_KEY, schoolId);
}

/**
 * Store the school's display name alongside its ID, so the login page can
 * show a human-readable label for a returning user instead of a bare UUID.
 */
export function setSchoolName(name: string): void {
  localStorage.setItem(SCHOOL_NAME_KEY, name);
}

/**
 * Clear all auth-related data from localStorage. Does NOT clear the
 * httpOnly refresh cookie — only the server can revoke that (via
 * POST /auth/logout). Deliberately leaves SCHOOL_ID_KEY/SCHOOL_NAME_KEY in
 * place: knowing a schoolId grants no access on its own, and clearing it on
 * every logout would force a returning user to retype their school UUID at
 * every login — the opposite of the login page's persisted-prefill purpose.
 */
export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  /*
   * [REQ-SEP-04 P0.5 / RC-5] The service worker's `api-cache` (runtime NetworkFirst) stores
   * authenticated GET responses KEYED BY URL ALONE — no Authorization in the key. Left in place
   * across a logout, a network blip or the 10s network timeout on a SHARED device serves the
   * PREVIOUS user's cached data into the next user's session. Best-effort fire-and-forget: the
   * caches API can be absent (non-SW contexts) or throw (blocked-storage configurations), and a
   * failed delete must never block a logout — the localStorage removals above are the part that
   * ends the session.
   */
  try {
    if (typeof caches !== 'undefined') void caches.delete('api-cache').catch(() => {});
  } catch { /* storage access can throw outright; the logout still stands */ }
}

/**
 * Create and configure Axios instance with interceptors.
 */
function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    // 27.10: required so the httpOnly refreshToken cookie is sent
    // automatically on /auth/refresh (and any other cookie-bearing path).
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  /**
   * Request interceptor: Attach JWT and schoolId to all requests.
   */
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getAccessToken();
      const schoolId = getSchoolId();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (schoolId) {
        config.headers['X-School-Id'] = schoolId;
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error),
  );

  /**
   * Response interceptor: Handle 401 via cookie-based refresh,
   * surface 409 as a marked Error, broadcast 5xx as toast events.
   */
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // ─── 401: cookie-based refresh + retry ──────────────────────────────
      const reqUrl = originalRequest.url ?? '';
      const isAuthCall = /\/(auth|owner)\/login/.test(reqUrl);
      if (error.response?.status === 401 && !originalRequest._retry && !isAuthCall) {
        // App Owner sessions carry no refresh cookie (the 8h access token stands
        // alone), so a 401 here is a genuinely ended session, not a refreshable
        // one. Send them to the owner login (re-login re-triggers TOTP) rather
        // than attempting a doomed /auth/refresh and bouncing to the school /login.
        if (getStoredUserRole() === 'APP_OWNER') {
          clearAuth();
          window.location.href = '/owner/login';
          return Promise.reject(error);
        }
        // Already refreshing — queue this request to retry after refresh completes.
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Refresh: empty body, the httpOnly cookie carries the refresh token.
          // We use a bare axios call (not the configured instance) to avoid
          // re-entering this same interceptor recursively.
          const refreshResponse = await axios.post<ApiResponse<{ accessToken: string }>>(
            `${API_BASE_URL}/auth/refresh`,
            {},
            { withCredentials: true },
          );

          const { accessToken } = refreshResponse.data.data;
          setAccessToken(accessToken);
          processQueue(null, accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          /*
           * [REQ-SEP-04 P0.1 / RC-1] Only a genuine auth failure signs the user out; a TRANSPORT
           * failure (no response) or a 5xx during the single-machine deploy swap must NOT — the
           * cookie + DB session are still valid. That was always the v49 spec. ⚠️ THE SHIPPED
           * FORM INVERTED IT: it failed open on everything except 401/403, and /auth/refresh
           * with a MISSING cookie returned 400 NO_REFRESH_TOKEN — so a dead session (the cookie
           * expires after 7 idle days) was treated as transient forever. The app rendered a
           * fully-logged-in shell where every query failed, and only logout+login recovered it —
           * the exact Bankeshwar "data not loading / something went wrong everywhere" report.
           *
           * Dead session = 401 · 403 · 400+NO_REFRESH_TOKEN (the last recognises the OLD
           * server's spelling during a deploy-window skew; the new server 401s it). Other 4xx
           * (a 429 burst on the shared limiter, a 408) stay transient — signing a user out for
           * being throttled would be a new defect wearing this fix's clothes.
           */
          processQueue(new Error('Token refresh failed'), null);
          const resp = (refreshError as AxiosError<ApiError>)?.response;
          const status = resp?.status;
          const refusalCode = (resp?.data as { error?: { code?: string } } | undefined)?.error?.code;
          const sessionDead =
            status === 401 || status === 403 || (status === 400 && refusalCode === 'NO_REFRESH_TOKEN');
          if (sessionDead) {
            clearAuth();
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // ─── 409: optimistic-lock conflict ─────────────────────────────────
      // Wrap into a marked Error so useConflictAwareMutation can detect it.
      // (See client/src/hooks/useConflictAwareMutation.ts.)
      if (error.response?.status === 409) {
        try { usageRefusalListener?.(); } catch { /* analytics must never break an error path */ }
        const conflictError = new Error(
          'Data modified by another user. Refresh to see latest.',
        ) as Error & { isConflict: boolean; response: typeof error.response };
        conflictError.isConflict = true;
        conflictError.response = error.response;
        return Promise.reject(conflictError);
      }

      // ─── 5xx: broadcast a toast event for global handling ──────────────
      if (error.response?.status && error.response.status >= 500) {
        window.dispatchEvent(
          new CustomEvent('api-error', {
            detail: {
              type: 'error',
              message:
                error.response.data?.message ||
                'Server error. Please try again later.',
            },
          }),
        );
      }

      const terminalStatus = error.response?.status;
      if (terminalStatus && terminalStatus >= 400 && terminalStatus < 500) {
        // [USAGE-1] A refusal the user actually saw — count it against the current screen.
        try { usageRefusalListener?.(); } catch { /* analytics must never break an error path */ }
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

/**
 * Configured Axios instance for all API calls.
 *
 * Features:
 * - Auto-attaches JWT access token (Authorization header)
 * - Auto-attaches X-School-Id header
 * - Sends httpOnly refresh cookie on cookie-bearing requests
 * - Auto-refreshes access token on 401 via cookie
 * - Queues parallel requests during refresh
 * - Marks 409 errors with isConflict for the conflict-aware hook
 * - Broadcasts 5xx as `api-error` window events
 */
export const api = createApiInstance();

export default api;